import { unwrap, type components } from "@wilco/api";
import { api } from "../auth.ts";
import { productInfos } from "../lookups.ts";

/*
 * Ремонт и «Виріб» на REST из contracts/openapi.json (модуль service).
 * «Виріб» по скану без цен (SRV-08); стоимость ремонта отдельным запросом
 * под правом себестоимости (ПС-13). Детали ремонта приходят с id товара:
 * названия из справочника.
 */
type S = components["schemas"];

export type Card = S["CardView"];
export type PartAction = S["RepairPartInput"]["action"];
export type RepairState = "CONFIRMED" | "UNDER_REPAIR" | "DONE" | "CANCELLED";

export interface Repair extends Omit<S["RepairCardView"], "state"> {
  state: RepairState;
  productName: string;
}

const id = (v: string) => ({ params: { path: { id: v } } });

async function enrich(r: S["RepairCardView"]): Promise<Repair> {
  const info = await productInfos([r.productId]);
  return { ...r, state: r.state as RepairState, productName: info.get(r.productId)?.name ?? r.productId };
}

const repairOf = async (p: Promise<{ data?: S["RepairCardView"]; error?: unknown; response: Response }>) => enrich(await unwrap(p));

export const serviceApi = {
  card: (code: string) => unwrap(api.GET("/api/v1/service/cards/scan", { params: { query: { code } } })),
  open: (serial: string, reason: string | null) => repairOf(api.POST("/api/v1/service/repairs", { body: { serial, reason } })),
  repair: (v: string) => repairOf(api.GET("/api/v1/service/repairs/{id}", id(v))),
  cost: (v: string) => unwrap(api.GET("/api/v1/service/repairs/{id}/cost", id(v))),
  constructor: (v: string) => unwrap(api.GET("/api/v1/service/repairs/{id}/constructor", id(v))),
  works: (v: string, body: S["RepairWorksInput"]) => repairOf(api.POST("/api/v1/service/repairs/{id}/works", { ...id(v), body })),
  part: (v: string, body: S["RepairPartInput"]) => repairOf(api.POST("/api/v1/service/repairs/{id}/parts", { ...id(v), body })),
  close: (v: string) => repairOf(api.POST("/api/v1/service/repairs/{id}/close", id(v))),
  cancel: (v: string) => repairOf(api.POST("/api/v1/service/repairs/{id}/cancel", id(v))),
  release: (serial: string) => unwrap(api.POST("/api/v1/service/units/{serial}/release", { params: { path: { serial } } })).then(() => null),
};
